import { requireConnection } from './connectivity';
import { Injectable } from '@angular/core';

export const IMAGE_ROLES: Record<string, string> = { unclassified: '미분류', natal: '원국', decadal: '대운', annual: '세운', mixed: '여러 역할' };
const uid = () => crypto.randomUUID().replace(/-/g, '');
export interface Attachment {
    id: string; name: string; mime: string; size: number; role: string; status: string;
    file?: File; preview?: string; error?: string; width?: number; height?: number; progress?: number;
    reading?: any; replaces?: string;
}

@Injectable({ providedIn: 'root' })
export class UploadSession {
    public beforeNavigate: (url: string) => Promise<boolean> = async () => true;
    public onUnauthorized: (token: string) => void = () => {};
    public changed: () => void = () => {};
    private epoch = 0;
    public review: any = null;
    public reviewConflict: any = null;
    public policy: any = null;
    public draft: any = null;
    public draftId = '';
    // Stable before the first upload, so photo selection keeps its identity.
    public contextId = uid();
    public list: any[] = [];
    public items: Attachment[] = [];
    public busy = false;
    public message = '';
    public comparison: any = null;
    private token = '';
    private savingInput?: Promise<void>;
    public async ensureDraft(input: any) {
        if (this.savingInput) await this.savingInput;
        if (!this.draft) await this.saveInput(input);
    }
    public get unsettled() { return this.items.some(i => i.status !== 'uploaded'); }
    public get sources() {
        return this.items.filter(i => i.status === 'uploaded').map(i => ({ id: 'image-' + i.id, kind: 'image',
            reference: 'saju-upload-v1:' + i.id, section: i.role === 'natal' ? 'natal' : ['decadal', 'annual'].includes(i.role) ? 'luck' : 'mixed' }));
    }
    public clear() {
        this.epoch++;
        this.contextId = uid();
        this.items.forEach(i => { if (i.preview) URL.revokeObjectURL(i.preview); });
        this.review = null; this.reviewConflict = null; this.items = []; this.draft = null; this.draftId = ''; this.message = ''; this.comparison = null;
    }
    public disconnect() { this.clear(); this.token = ''; this.policy = null; this.list = []; }
    private formatHelp(file: File) {
        if (/\.hei[cf]$/i.test(file.name) || /image\/hei[cf]/i.test(file.type))
            return 'HEIC·HEIF 사진은 바로 읽을 수 없어요. 사진을 열어 화면을 캡처하거나 JPEG로 저장해 주세요.';
        if (/\.pdf$/i.test(file.name) || file.type === 'application/pdf')
            return 'PDF는 바로 읽을 수 없어요. 만세력 표가 있는 페이지를 열어 화면을 캡처해 주세요.';
        if (/\.gif$/i.test(file.name) || file.type === 'image/gif')
            return 'GIF는 바로 읽을 수 없어요. 글자가 잘 보이는 장면을 캡처해 주세요.';
        return '이 형식은 바로 읽을 수 없어요. 사진을 열어 화면을 캡처하거나 PNG·JPEG·WebP로 저장해 주세요.';
    }
    private requestError(status: number, result: any = {}) {
        const code = result?.error_code || '';
        const message = status === 413 || code === 'FILE_SIZE' ? '파일이 너무 큽니다. 10MiB 이하 사진으로 바꿔 주세요.' :
            code === 'ANIMATED_IMAGE' ? '움직이는 사진은 읽을 수 없어요. 글자가 잘 보이는 장면을 캡처해 주세요.' :
            code === 'FILE_TYPE_MISMATCH' ? '파일 이름과 실제 형식이 달라요. 확장자만 바꾸지 말고 사진을 열어 PNG·JPEG·WebP로 다시 저장해 주세요.' :
            code === 'FILE_TYPE' || status === 415 ? '이 형식은 읽을 수 없어요. 사진을 열어 화면을 캡처하거나 PNG·JPEG·WebP로 저장해 주세요.' :
            code === 'DAMAGED_IMAGE' ? '사진을 끝까지 읽을 수 없습니다. 원본을 다시 캡처하거나 새 사진으로 바꿔 주세요.' :
            code === 'PIXEL_LIMIT' ? '사진 크기나 픽셀 수가 지원 범위를 벗어났습니다. 글자가 선명하게 보이는 지원 크기의 사진으로 바꿔 주세요.' :
            status === 409 ? '다른 곳에서 입력을 수정했어요. 최신 내용과 비교한 뒤 다시 시도해 주세요.' :
            [400,422].includes(status) ? '사진 내용을 확인할 수 없습니다. 이 사진만 바꾸고 다른 첨부는 그대로 둘 수 있습니다.' :
            [401,403].includes(status) ? '저장 연결을 다시 확인해 주세요.' :
            status === 404 ? '사진이나 작성 중인 내용을 찾을 수 없어요. 내 만세력에서 다시 확인해 주세요.' :
            '첨부 처리를 확인하지 못했습니다. 선택한 사진은 유지됩니다. 같은 작업을 다시 시도해 주세요.';
        return Object.assign(new Error(message), { status, code, current: result?.current });
    }
    private async request(path: string, method = 'GET', body?: any, binary = false) {
        requireConnection();
        const epoch = this.epoch, token = this.token;
        const controller = new AbortController(); const timer = setTimeout(() => controller.abort(), 30000);
        try {
            const isFile = body instanceof File;
            const response = await fetch('/api/saju/uploads' + path, { method, cache: 'no-store', credentials: 'include',
                headers: { Authorization: 'Bearer ' + this.token, ...(body ? isFile ? { 'Content-Type': body.type || 'application/octet-stream', 'X-File-Name': encodeURIComponent(body.name) } : { 'Content-Type': 'application/json' } : {}) },
                body: body ? isFile ? body : JSON.stringify(body) : undefined, signal: controller.signal });
            const result = response.ok && binary ? await response.blob() : await response.json();
            if (epoch !== this.epoch || token !== this.token) throw {stale:true};
            if ([401,403].includes(response.status)) this.onUnauthorized(token);
            if (response.ok && binary) return result;
            if (!response.ok) throw this.requestError(response.status, result);
            return result.data;
        } catch (error: any) {
            if (error.stale || error.status) throw error;
            throw new Error('첨부 전송을 확인하지 못했습니다. 입력과 선택한 파일은 유지됩니다. 연결을 확인하고 다시 시도해 주세요.');
        } finally { clearTimeout(timer); }
    }
    private uploadRequest(path: string, item: Attachment) {
        requireConnection();
        const epoch = this.epoch, token = this.token, file = item.file!;
        return new Promise<any>((resolve, reject) => {
            const request = new XMLHttpRequest();
            request.open('PUT', '/api/saju/uploads' + path);
            request.withCredentials = true;
            request.timeout = 30000;
            request.setRequestHeader('Authorization', 'Bearer ' + token);
            request.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
            request.setRequestHeader('X-File-Name', encodeURIComponent(file.name));
            request.upload.onprogress = event => {
                if (epoch !== this.epoch || token !== this.token || !this.items.includes(item)) return;
                item.progress = event.lengthComputable && event.total ? Math.min(99, Math.round(event.loaded / event.total * 100)) : undefined;
                this.changed();
            };
            const connectionFailure = () => reject(new Error('사진을 보내는 중 네트워크 연결이 끊겼습니다. 선택한 사진은 유지됩니다. 연결을 확인한 뒤 이 사진만 다시 시도해 주세요.'));
            request.onerror = connectionFailure;
            request.ontimeout = connectionFailure;
            request.onabort = connectionFailure;
            request.onload = () => {
                if (epoch !== this.epoch || token !== this.token) { reject({ stale: true }); return; }
                let result: any = {};
                try { result = request.responseText ? JSON.parse(request.responseText) : {}; } catch {}
                if ([401,403].includes(request.status)) this.onUnauthorized(token);
                if (request.status < 200 || request.status >= 300) { reject(this.requestError(request.status, result)); return; }
                resolve(result.data);
            };
            request.send(file);
        });
    }
    public async connect(token: string) {
        if (this.token !== token) this.clear();
        this.token = token;
        this.policy = await this.request('/policy');
        this.list = await this.request('/drafts');
    }
    public async saveInput(input: any) {
        const previous = this.savingInput, epoch = this.epoch;
        const fixed = JSON.parse(JSON.stringify(input));
        const work = (async () => {
            // Serialize every queued writer, including three or more callers.
            if (previous) await previous.catch(() => {});
            if (epoch !== this.epoch) throw { stale: true };
            await this.saveInputNow(fixed);
        })();
        this.savingInput = work;
        try { await work; } finally { if (this.savingInput === work) this.savingInput = undefined; }
    }
    private async saveInputNow(input: any) {
        if (!this.draftId) this.draftId = this.contextId;
        try {
            // Preserve legacy editor fields; review history stays server-owned.
            const merged = { ...(this.draft?.input || {}), ...input };
            delete merged._review;
            this.draft = await this.request('/drafts/' + this.draftId, 'PUT', { input: merged, version: this.draft?.version || 0 });
            this.comparison = null;
            this.list = await this.request('/drafts');
        } catch (e: any) {
            if (e.code === 'DRAFT_CONFLICT') this.comparison = await this.request('/drafts/' + this.draftId);
            throw e;
        }
    }
    public acceptComparedBase() {
        if (this.comparison) { this.draft = this.comparison; this.comparison = null; }
    }
    private async previews() {
        for (const item of this.items) {
            if (!item.preview && item.status === 'uploaded') {
                try { item.preview = URL.createObjectURL(await this.request('/images/' + item.id, 'GET', undefined, true)); item.error = ''; }
                catch { item.error = '미리보기를 불러오지 못했습니다. 다시 시도해 주세요.'; }
            }
        }
    }
    private async apply(draft: any, keepFailed = true) {
        const previous = this.items;
        if (!this.draft || this.draft.id !== draft.id || draft.version >= this.draft.version) this.draft = draft;
        this.draftId = draft.id; this.contextId = draft.id;
        this.items = draft.images.map((i: any) => ({ ...i, preview: previous.find(p => p.id === i.id)?.preview,
            reading: previous.find(p => p.id === i.id)?.reading, status: 'uploaded', progress: 100 }));
        if (keepFailed) this.items.push(...previous.filter(p => p.status !== 'uploaded' && !this.items.some(i => i.id === p.id)));
        for (const p of previous) if (p.preview && !this.items.some(i => i.preview === p.preview)) URL.revokeObjectURL(p.preview);
        await this.previews();
    }
    public async open(id: string, current: () => boolean = () => true) {
        const draft = await this.request('/drafts/' + id);
        if (!current()) throw { stale: true };
        this.clear(); await this.apply(draft, false);
        if (!current()) throw { stale: true };
        for (const item of this.items) if (!await this.readingRequest(item) || !current()) throw { stale: true };
        if (draft.input._review || this.items.some(i => i.reading)) await this.loadReview();
        if (!current()) throw { stale: true };
        return draft.input;
    }
    private selected(file: File): Attachment {
        return { id: uid(), name: file.name, mime: file.type, size: file.size, role: 'unclassified', status: 'selected', file, progress: 0,
            preview: this.policy.mimes.includes(file.type) && file.size <= this.policy.max_bytes ? URL.createObjectURL(file) : undefined };
    }
    public select(files: FileList | File[]) {
        for (const file of Array.from(files)) {
            if (this.items.length >= this.policy.max_images) { this.message = '사진은 한 번에 최대 ' + this.policy.max_images + '장까지 올릴 수 있어요.'; break; }
            this.items.push(this.selected(file));
        }
    }
    public async uploadOne(item: Attachment) {
        if (!item.file) { await this.previews(); return; }
        item.status = 'uploading'; item.error = ''; item.progress = 0; this.changed();
        try {
            if (!this.policy.mimes.includes(item.file.type)) throw new Error(this.formatHelp(item.file));
            if (!item.file.size) throw new Error('파일이 비어 있어요. 사진을 다시 선택해 주세요.');
            if (item.file.size > this.policy.max_bytes) throw new Error('파일이 너무 커요. ' + (this.policy.max_bytes / 1048576) + 'MiB 이하 사진으로 바꿔 주세요.');
            const data = await this.uploadRequest('/drafts/' + this.draftId + '/images/' + item.id, item);
            item.status = 'uploaded'; item.progress = 100;
            if (data.duplicate) this.message = '동일한 파일이 이미 첨부되어 있습니다. 기존 이미지를 유지했습니다.';
            await this.apply(data.draft);
            if (item.replaces && data.image_id !== item.replaces) {
                const previous = this.items.find(i => i.id === item.replaces);
                if (previous) await this.remove(previous);
            }
        } catch (e: any) {
            if (e?.stale || !this.items.includes(item)) return;
            item.status = 'failed'; item.progress = 0;
            item.error = e.message || '전송을 확인하지 못했습니다. 선택한 사진은 유지됩니다. 같은 파일로 다시 시도해 주세요.';
            this.changed();
        }
    }
    public async replace(item: Attachment, file: File) {
        const index = this.items.indexOf(item);
        if (index < 0) throw new Error('바꿀 사진을 찾을 수 없습니다. 다시 선택해 주세요.');
        const replacement = this.selected(file);
        if (item.status === 'uploaded') {
            // Keep the readable original until its replacement has been stored.
            replacement.replaces = item.id;
            this.items.splice(index + 1, 0, replacement);
            return replacement;
        }
        await this.remove(item);
        this.items.splice(Math.min(index, this.items.length), 0, replacement);
        this.message = '문제가 있던 사진만 새 파일로 바꿨습니다. 이미 올라간 다른 사진은 유지됩니다.';
        return replacement;
    }
    public async edit(items: Attachment[]) {
        const draft = await this.request('/drafts/' + this.draftId + '/images', 'PUT', {
            version: this.draft.version, images: items.filter(i => i.status === 'uploaded').map(i => ({ id: i.id, role: i.role })) });
        await this.apply(draft);
    }
    public async remove(item: Attachment) {
        if (this.draft) {
            const result = await this.request('/drafts/' + this.draftId + '/images/' + item.id, 'DELETE');
            this.items = this.items.filter(i => i.id !== item.id);
            if (item.preview) URL.revokeObjectURL(item.preview);
            await this.apply(result.draft);
            this.message = result.retained.length ? '이 입력에서 사진을 제거했어요. 다른 저장 자료에서 쓰는 사진은 남습니다.' : '사진을 제거했어요.';
        } else { this.items = this.items.filter(i => i.id !== item.id); if (item.preview) URL.revokeObjectURL(item.preview); }
    }
    public async loadReview() {
        this.review = await this.request('/drafts/' + this.draftId + '/review');
        if (this.draft) this.draft.version = this.review.version;
    }
    public async decide(action: any) {
        try {
            this.review = await this.request('/drafts/' + this.draftId + '/review', 'PUT', { token: this.review.token, action });
            this.draft.version = this.review.version; this.reviewConflict = null;
        } catch (e: any) { if (e.code === 'REVIEW_CONFLICT') this.reviewConflict = e.current; throw e; }
    }
    public readingMessage(item: Attachment) {
        const result = item.reading?.result;
        if (!result) return '풀이 시작 시 사진을 읽습니다';
        if (result.status === 'running') return '판독 중 · 제안 조회로 진행 상태를 확인해 주세요.';
        if (result.automatic_validation?.natal_usable) return '사진 읽기·검사 완료 · 읽힌 값 사용 가능';
        if (result.status === 'read') return '사진 읽기 완료 · 필요한 부분만 확인해 주세요.';
        if (result.status === 'unclear') return '일부 글자·배열을 읽지 못했습니다. 미확인 후보를 원본과 비교해 주세요.';
        const messages: Record<string, string> = {
            READER_UNAVAILABLE: '판독 서비스 연결이 준비되지 않았습니다. 사진은 그대로 보관됩니다.',
            AUTHENTICATION_ERROR: '판독 서비스 연결을 확인해야 합니다. 사진은 그대로 보관됩니다.',
            TIMEOUT: '판독 응답 시간이 초과되었습니다. 처리 여부 확인 전에는 다시 요청하지 않습니다.',
            TRANSPORT_ERROR: '판독 서비스와 통신하지 못했습니다. 처리 여부 확인이 필요합니다.',
            ATTEMPT_INTERRUPTED: '판독 처리가 중단되어 서비스 확인이 필요합니다. 사진은 그대로 보관됩니다.',
            NO_TEXT: '사진에서 글자를 찾지 못했습니다. 글자가 있는 만세력 사진인지 확인해 주세요.',
            READING_LIMIT_REACHED: '판독 서비스의 호출 한도에 도달했습니다. 사진은 그대로 보관됩니다.',
            SERVICE_REQUEST_REJECTED: '판독 서비스가 요청을 처리하지 못했습니다. 서비스 설정 확인이 필요하며 사진은 보관됩니다.',
            UNSUPPORTED_INPUT: '이 사진의 형식이나 내용을 판독할 수 없습니다. 선명한 캡처로 준비해 주세요.',
            PROVIDER_FAILURE: '판독 서비스에서 오류가 발생했습니다. 원본은 보존됩니다.',
            RATE_LIMITED: '판독 요청이 잠시 몰렸습니다. 잠시 후 허용된 재시도를 이용해 주세요.',
            QUOTA_EXCEEDED: '판독 서비스의 사용 한도를 확인해야 합니다. 원본은 보존됩니다.'
        };
        return messages[result.error_code] || '판독 서비스 응답을 확인하지 못했습니다. 사진은 그대로 보관됩니다.';
    }
    get hasReadingServiceError() {
        return this.items.some(item => item.reading?.result?.error_kind === 'service');
    }
    private async readingRequest(item: Attachment, retry?: boolean) {
        requireConnection();
        const epoch = this.epoch, token = this.token;
        const response = await fetch('/api/saju/readings/' + item.id, { method: retry === undefined ? 'GET' : 'POST', cache: 'no-store', credentials: 'include',
            headers: { Authorization: 'Bearer ' + this.token, 'Content-Type': 'application/json' },
            body: retry === undefined ? undefined : JSON.stringify({ retry }) });
        const result = await response.json();
        if (epoch !== this.epoch || token !== this.token) return false;
        if ([401,403].includes(response.status)) this.onUnauthorized(token);
        if (!response.ok && !result.data) {
            if (response.status >= 500 || result.error_kind === 'service') {
                item.reading = { result: { status: 'failed', error_code: result.error_code,
                    error_kind: 'service', review_required: false }, can_retry: false };
                this.message = this.readingMessage(item);
            }
            throw new Error(this.message || '판독 서비스 요청을 확인하지 못했습니다. 사진은 보관됩니다.');
        }
        item.reading = result.data;
        return true;
    }
    public async readImage(item: Attachment, retry = false) {
        if (!await this.readingRequest(item, retry)) return;
        this.message = this.readingMessage(item);
        await this.loadReview();
    }
    public async deleteDraft() {
        if (this.draft) await this.request('/drafts/' + this.draftId, 'DELETE');
        this.clear(); this.list = await this.request('/drafts'); this.message = '작성 중인 내용을 삭제했어요. 저장한 사주에서 쓰는 사진은 남습니다.';
    }
}
